import { Box, Divider, Typography, styled, useTheme } from '@mui/material'
import React, { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ICustomerFormSchema,
  IFormSchemaReport,
  IFormSchemaReportDeviationDto,
  formSchemaReportTypes,
} from 'src/generated-types/form-schema'
import { FlexSpinner } from 'src/components/Spinner'
import { SingleChecklistForm } from './SingleChecklistForm'
import { isEmptyReportValues } from './checklist-utils'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

interface IMovementChecklistsProps {
  customerId: EntityId
  schemas: ICustomerFormSchema[]
  isLoading?: boolean
  onChange: (reports: IFormSchemaReport[], isComplete: boolean) => void
  mainComponentId?: EntityId
}

export const MovementChecklists: React.FC<IMovementChecklistsProps> = ({
  customerId,
  schemas,
  isLoading,
  onChange,
  mainComponentId,
}) => {
  const theme = useTheme()
  const { t } = useTranslation('checklist')

  const checklistsStateRef = useRef<
    Record<
      number,
      {
        versionId: number
        values: Record<string, any>
        isValid: boolean
        formSchemaReportDeviations?: IFormSchemaReportDeviationDto[]
      }
    >
  >({})

  const handleChecklistChange = useCallback(
    (
      schemaId: number,
      versionId: number,
      values: Record<string, any>,
      isValid: boolean,
      formSchemaReportDeviations?: IFormSchemaReportDeviationDto[],
    ) => {
      checklistsStateRef.current = {
        ...checklistsStateRef.current,
        [schemaId]: { versionId, values, isValid, formSchemaReportDeviations },
      }

      const stateEntries = Object.values(checklistsStateRef.current)
      const allValid = stateEntries.length > 0 && stateEntries.every(s => s.isValid)
      const reports: IFormSchemaReport[] = stateEntries
        .filter(s => !isEmptyReportValues(s.values) || (s.formSchemaReportDeviations?.length ?? 0) > 0)
        .map(
          s =>
            ({
              formSchemaVersionId: s.versionId,
              values: JSON.stringify(s.values),
              type: formSchemaReportTypes.MainComponent,
              formSchemaReportDeviations: s.formSchemaReportDeviations,
            } as IFormSchemaReport),
        )

      onChange(reports, allValid)
    },
    [onChange],
  )

  if (isLoading) {
    return (
      <StyledLoadingBox>
        <FlexSpinner />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('loading-checklists')}
        </Typography>
      </StyledLoadingBox>
    )
  }

  return (
    <StyledContainer>
      {schemas?.map((schema, index) => (
        <StyledChecklistSection key={schema.id}>
          <Typography
            variant="subtitle2"
            fontWeight={500}
            sx={{ mb: 1, color: getThemeColor(theme, MUIThemeColors.primaryMain) }}
          >
            {schema.name}
          </Typography>
          <SingleChecklistForm
            schema={schema}
            onChangeValues={handleChecklistChange}
            customerId={customerId}
            mainComponentId={mainComponentId}
            collectDeviationsWithoutSaving={true}
          />
          {index < schemas.length - 1 && <Divider sx={{ mt: 2 }} />}
        </StyledChecklistSection>
      ))}
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}))

const StyledChecklistSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: theme.spacing(2),
}))

const StyledLoadingBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 0',
}))
